import React from 'react';
import { Plus, Minus, X } from 'lucide-react';
import { config } from '../../config/environment';

interface Produto {
  id: number;
  nome: string;
  descricao: string;
  preco: number;
  imagem_url?: string;
  codigo?: string;
  venda_por_peso?: boolean | number;
}

interface DesktopProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  produto: Produto | null;
  quantidade: number;
  observacoes: string;
  onQuantidadeChange: (qtd: number) => void;
  onObservacoesChange: (obs: string) => void;
  peso: number | null;
  onPesoChange: (peso: number | null) => void;
  onAdicionar: () => void;
  onCancelar: () => void;
}

const DesktopProductModal: React.FC<DesktopProductModalProps> = ({
  isOpen,
  onClose,
  produto,
  quantidade,
  observacoes,
  onQuantidadeChange,
  onObservacoesChange,
  peso,
  onPesoChange,
  onAdicionar,
  onCancelar
}) => {
  if (!isOpen || !produto) return null;

  const isPeso = !!produto.venda_por_peso;
  const total = isPeso 
    ? (produto.preco * (peso || 0)) 
    : (produto.preco * quantidade);

  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl w-full max-w-md border border-gray-600/50 shadow-2xl max-h-[90vh] overflow-y-auto scroll-smooth"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex justify-between items-start">
            <h2 className="text-xl font-bold text-white pr-4 flex-1 min-w-0 leading-tight">
              {produto.nome}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-all duration-200 touch-manipulation active:scale-95 focus:ring-2 focus:ring-gray-500/50 focus:outline-none flex-shrink-0 p-1"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Imagem */}
          {produto.imagem_url && (
            <div className="w-full h-48 bg-gray-700 rounded-xl overflow-hidden">
              <img
                src={produto.imagem_url.startsWith('http') 
                  ? produto.imagem_url 
                  : `${config.api.baseUrl || window.location.origin}${produto.imagem_url.startsWith('/') ? '' : '/'}${produto.imagem_url}`}
                alt={produto.nome}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Código */}
          {produto.codigo && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">Código:</span>
              <span className="bg-purple-500/20 text-purple-300 text-xs px-2 py-1 rounded border border-purple-500/30 font-mono">
                {produto.codigo}
              </span>
            </div>
          )}

          {/* Descrição */}
          {produto.descricao && (
            <p className="text-gray-300 text-sm leading-relaxed">
              {produto.descricao}
            </p>
          )}

          {/* Quantidade ou Peso */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {isPeso ? 'Peso (KG)' : 'Quantidade'}
            </label>
            
            {isPeso ? (
              <div className="relative">
                <input
                  type="tel"
                  inputMode="numeric"
                  value={peso === null ? '' : peso.toFixed(3).replace('.', ',')}
                  onChange={(e) => {
                    // Remove tudo que não for dígito
                    const apenasNumeros = e.target.value.replace(/\D/g, '');
                    if (!apenasNumeros) {
                      onPesoChange(null);
                      return;
                    }
                    // Divide por 1000 para gerar as 3 casas decimais
                    const valorNum = parseInt(apenasNumeros, 10) / 1000;
                    onPesoChange(valorNum);
                  }}
                  placeholder="0,000"
                  autoFocus
                  className="w-full px-4 py-4 bg-gray-700/50 border-2 border-orange-500/50 rounded-xl text-white text-3xl font-black text-center placeholder-gray-600 focus:outline-none focus:border-orange-500 transition-all shadow-lg shadow-orange-500/10"
                />
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                  <span className="text-orange-500 font-black text-xl">KG</span>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={() => onQuantidadeChange(Math.max(1, quantidade - 1))}
                  className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center text-white hover:bg-gray-500 transition-all duration-200 hover:shadow-lg touch-manipulation active:scale-95 focus:ring-2 focus:ring-gray-500/50 focus:outline-none"
                  aria-label="Diminuir quantidade"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="text-white font-medium text-xl min-w-[40px] text-center leading-tight">
                  {quantidade}
                </span>
                <button
                  onClick={() => onQuantidadeChange(quantidade + 1)}
                  className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center text-white hover:bg-gray-500 transition-all duration-200 hover:shadow-lg touch-manipulation active:scale-95 focus:ring-2 focus:ring-gray-500/50 focus:outline-none"
                  aria-label="Aumentar quantidade"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          {/* Observações */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Observações
            </label>
            <textarea
              value={observacoes}
              onChange={(e) => onObservacoesChange(e.target.value)}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-200 text-base touch-manipulation leading-normal"
              placeholder="Ex: Sem cebola, bem assado..."
              rows={3}
              style={{ fontSize: '16px' }}
            />
          </div>

          {/* Total */}
          <div className="bg-gray-700/50 rounded-xl p-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-300 text-sm">Total:</span>
              <span className="text-orange-400 font-bold text-lg">
                R$ {total.toFixed(2)}
              </span>
            </div>
            {isPeso && (
              <div className="text-right mt-2">
                <span className="text-[10px] text-gray-400">Preço por KG: R$ {produto.preco.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* Botões */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onCancelar}
              className="flex-1 bg-gray-600 text-white py-3 rounded-xl font-bold hover:bg-gray-700 transition-all duration-200 active:scale-95 focus:ring-2 focus:ring-gray-500/50 focus:outline-none touch-manipulation min-h-[44px] text-sm sm:text-base"
            >
              Cancelar
            </button>
            <button
              onClick={onAdicionar}
              disabled={isPeso && (!peso || peso <= 0)}
              className={`flex-1 py-3 rounded-xl font-bold transition-all duration-200 active:scale-95 focus:ring-2 focus:outline-none touch-manipulation min-h-[44px] text-sm sm:text-base ${
                isPeso && (!peso || peso <= 0)
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600 focus:ring-orange-500/50'
              }`}
            >
              Adicionar à Comanda
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesktopProductModal;
